// Mail einem Schaden zuordnen — Rückfrage zu den Anhängen (User 04.11.2026:
// „Wenn ich eine Mail einem Schaden zuweisen möchte, dann müssen wir fragen,
// ob die Anhänge mit gespeichert werden sollen oder nicht.“).
//
// Alle Wege („Einem Schaden zuordnen…“ aus Liste, Lesefenster, Kontextmenü
// und Mobil sowie „Neuer Schaden“ / „Einem bestehenden Schaden zuordnen“ in
// schaden@) laufen durch das Fenster `SchadenAblageFenster`; dort wird vor
// dem Ablegen hierüber gefragt. Popup und Anhangsliste sind dieselben wie
// beim Ziehen einer Mail in die Dokumente (`anhangFrage`).
import { frageNachAnhaengen, type AnhangFrage } from "../documents/ablage/anhangFrage";
import { postfachMailAnhaenge, type PostfachAnhaenge } from "../documents/ablage/mailAnhangAblage";

/** Was der Server zusätzlich zur Mail ablegen soll. */
export type SchadenAnhangAuswahl =
    /** Alle Anhänge (auch: keine Anhänge, Frage entfiel). */
    | { ohneAnhaenge?: undefined; anhaenge?: undefined }
    /** Nur die Mail — die Anhänge bleiben in der Mail-Datei. */
    | { ohneAnhaenge: true; anhaenge?: undefined }
    /** Nur diese Anhänge (Dateinamen, doppelte Namen mehrfach). */
    | { ohneAnhaenge?: undefined; anhaenge: string[] };

export const SCHADEN_ANHANG_TITEL = "Anhänge mit am Schaden speichern?";

/** Frage im Popup — je nachdem, ob die Anhänge bekannt sind. */
export function schadenAnhangFrageText(anzahl: number, unbekannt: boolean): string {
    if (unbekannt) {
        return "Diese Mail ist verschlüsselt oder signiert – ob sie Anhänge hat, zeigt sich erst beim Ablegen. Sollen ihre Anhänge mit im Schaden-Ordner gespeichert werden?";
    }
    return anzahl === 1
        ? "Diese Mail hat 1 Anhang. Soll er mit im Schaden-Ordner gespeichert werden?"
        : `Diese Mail hat ${anzahl} Anhänge. Sollen sie mit im Schaden-Ordner gespeichert werden?`;
}

/**
 * Fragt vor dem Zuordnen an einen Schaden, ob die Anhänge mitgespeichert
 * werden. `null` = abgebrochen, es passiert nichts. Ohne Anhänge (Bilder im
 * Mailtext zählen nicht) keine Frage. Lassen sich die Anhänge nicht laden,
 * bleibt es beim bisherigen Verhalten: Mail samt Anhängen.
 */
export async function frageSchadenAnhaenge(
    mail: { mailbox: string; messageId: string; betreff: string | null },
    frage: AnhangFrage = frageNachAnhaengen,
    laden: (mailbox: string, messageId: string) => Promise<PostfachAnhaenge | null> = postfachMailAnhaenge,
): Promise<SchadenAnhangAuswahl | null> {
    const info = await laden(mail.mailbox, mail.messageId);
    if (!info || (info.anhaenge.length === 0 && !info.unbekannt)) return {};

    const wahl = await frage({
        mailName: mail.betreff?.trim() || "Ohne Betreff",
        anhaenge: info.anhaenge.map((a) => ({ schluessel: a.id, name: a.name, groesse: a.size })),
        anzahlUnbekannt: info.unbekannt,
        titel: SCHADEN_ANHANG_TITEL,
        frage: schadenAnhangFrageText(info.anhaenge.length, info.unbekannt),
    });
    if (wahl.art === "abbrechen") return null;
    if (wahl.art === "nurMail") return { ohneAnhaenge: true };
    if (wahl.alle) return {};
    const gewaehlt = new Set(wahl.schluessel);
    const namen = info.anhaenge.filter((a) => gewaehlt.has(a.id)).map((a) => a.name);
    return namen.length > 0 ? { anhaenge: namen } : { ohneAnhaenge: true };
}
